import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Notification from '@rio-cloud/rio-uikit/Notification';
import StepButton from '@rio-cloud/rio-uikit/StepButton';
import { type FieldErrors, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { apiSchema } from '#api/model/types.ts';
import { useCreateOrderMutation } from '#api-bridge/orderApi.ts';
import { reportErrorToSentry } from '#configuration/setup/sentry.ts';
import { mapWriteOrder } from '#utils/orders.util.ts';

import type { OrderFormData, OrderFormPage } from './OrderFormValidation.ts';

type CreateOrderButtonToolbarProps = {
    currentFormPage: OrderFormPage;
    setActiveOrder: (orderId: string) => void;
    showMaterial: () => void;
    showLoadingStop: () => void;
    showUnloadingStop: () => void;
    onValidationErrors: (errors: FieldErrors<OrderFormData>) => void;
    handleClose: () => void;
};

export const CreateOrderButtonToolbar = ({
    currentFormPage,
    setActiveOrder,
    showMaterial,
    showLoadingStop,
    showUnloadingStop,
    onValidationErrors,
    handleClose,
}: CreateOrderButtonToolbarProps) => {
    const intl = useIntl();
    const { reset, handleSubmit } = useFormContext<OrderFormData>();
    const [createOrder, { isLoading }] = useCreateOrderMutation();

    const submitOrder = (createWithMaterial: boolean, closeOnSuccess: boolean) => {
        return handleSubmit(
            async formValues => {
                try {
                    const values = {
                        ...formValues,
                        materials: createWithMaterial ? formValues.materials : [],
                    };
                    const writeOrder = apiSchema.WriteOrder.parse(mapWriteOrder(values));
                    const createdOrder = await createOrder(writeOrder).unwrap();
                    setActiveOrder(createdOrder.id);
                    Notification.success(
                        intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.success',

                            defaultMessage: 'Order successfully created!',
                        })
                    );

                    if (closeOnSuccess) {
                        handleClose();
                    }
                } catch (e) {
                    reportErrorToSentry(e);
                }
            },
            errors => {
                onValidationErrors(errors);
                showCreateErrorNotification();
            }
        );
    };

    const handleComplete = async (createWithMaterial = true, closeOnSuccess = true) => {
        await submitOrder(createWithMaterial, closeOnSuccess)();
    };

    const showCreateErrorNotification = () => {
        Notification.error(
            intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.error',
                defaultMessage: 'The order could not be created. Please correct the errors in the form and try again.',
            })
        );
    };

    const step1splitButtonItems = [
        {
            value: intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.step1-split-button.create.no-freight',

                defaultMessage: 'Create without freight',
            }),
            onSelect: () => handleComplete(false).catch(reportErrorToSentry),
        },
    ];

    const step2splitButtonItems = [
        {
            value: intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.step2-split-button.create.new',

                defaultMessage: 'Create & new order',
            }),
            onSelect: () => {
                handleComplete(true, false).then(() => {
                    reset();
                    showLoadingStop();
                });
            },
        },
    ];

    return (
        <>
            {currentFormPage === 'loadingStop' && (
                <ButtonToolbar alignment={'right'}>
                    <StepButton direction='next' bsStyle='primary' onClick={showUnloadingStop}>
                        <FormattedMessage
                            id={'intl-msg:ordercom-cloud-frontend.orders.create-dialog.footer.next.load'}
                        />
                    </StepButton>
                </ButtonToolbar>
            )}
            {currentFormPage === 'unloadingStop' && (
                <ButtonToolbar alignment={'space-between'} className={'width-100pct'}>
                    <StepButton direction='previous' bsStyle='default' onClick={showLoadingStop}>
                        <FormattedMessage id={'intl-msg:ordercom-cloud-frontend.orders.create-dialog.footer.back'} />
                    </StepButton>

                    <ButtonDropdown
                        disabled={isLoading}
                        title={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.footer.add.load',

                            defaultMessage: 'Continue to cargo',
                        })}
                        bsStyle='primary'
                        dropdownClassName={'white-space-nowrap'}
                        pullRight
                        items={step1splitButtonItems}
                        splitButton
                        onLabelButtonClick={showMaterial}
                    />
                </ButtonToolbar>
            )}
            {currentFormPage === 'orderMaterial' && (
                <ButtonToolbar alignment={'space-between'} className={'width-100pct'}>
                    <StepButton direction='previous' bsStyle='default' onClick={showUnloadingStop}>
                        <FormattedMessage id={'intl-msg:ordercom-cloud-frontend.orders.create-dialog.footer.back'} />
                    </StepButton>
                    <ButtonDropdown
                        disabled={isLoading}
                        title={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.step2-split-button.create',

                            defaultMessage: 'Create',
                        })}
                        bsStyle='primary'
                        dropdownClassName={'white-space-nowrap'}
                        pullRight
                        items={step2splitButtonItems}
                        splitButton
                        onLabelButtonClick={() => handleComplete().catch(reportErrorToSentry)}
                    />
                </ButtonToolbar>
            )}
        </>
    );
};
